// useIsMobile — single source of truth for mobile detection.
// Until now every file defined its own copy (or, more often, didn't have one
// at all). Components import this and re-render when the viewport crosses the
// breakpoint.
//
// Breakpoints (matching common iPhone/iPad layouts):
//   isMobile   → ≤ 768 px (phones + small tablets in portrait)
//   isTablet   → 769–1024 px (iPad portrait)
//   isCompact  → ≤ 1024 px (anything where multi-column desktop UI breaks)

import { useState, useEffect } from "react";

export function useIsMobile(breakpoint = 768) {
  const [is, setIs] = useState(() => typeof window !== "undefined" && window.innerWidth <= breakpoint);
  useEffect(() => {
    const onResize = () => setIs(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize, { passive: true });
    // Also listen to `orientationchange` — iOS doesn't always fire `resize` immediately on rotate
    window.addEventListener("orientationchange", onResize, { passive: true });
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [breakpoint]);
  return is;
}

export function useIsTablet() {
  const [is, setIs] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth > 768 && window.innerWidth <= 1024;
  });
  useEffect(() => {
    const onResize = () => setIs(window.innerWidth > 768 && window.innerWidth <= 1024);
    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("orientationchange", onResize, { passive: true });
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);
  return is;
}

export function useIsCompact() {
  // True for phones AND iPad portrait — i.e. anywhere a 2-pane desktop layout
  // would feel cramped. Components that have wide-screen split views use this.
  return useIsMobile(1024);
}

export default useIsMobile;
